'use client'

import { useCallback, useMemo } from 'react'
import StakedRewardAmountText from './StakedRewardAmountText'
import RowCatName from './Row'
import { StakedListing } from './HoldingDetails.type'
import { useTranslation } from 'react-i18next'
import { Holding } from '@hooks/useHoldings'
import Table from '@components/Furry/Table'
import ClaimSolButton from './ClaimSolButton'
import ClaimAllStakingRewardsButton from './ClaimAllStakingRewardsButton'
import StakingBadge from './StakingBadge'

type StakingTableProps = {
  stakedListings: StakedListing[]
  holding: Holding
  onClaimAll?: () => void
}

const StakingTable = ({
  stakedListings,
  holding,
  onClaimAll,
}: StakingTableProps) => {
  const { t } = useTranslation()

  const getStatus = useCallback(
    (status: string) => {
      const name = t(`holdings.details.status.${status}`)
      let className

      switch (status) {
        case 'staking':
          className =
            'font-bold text-[#222529] px-2.5 py-1 text-sm bg-[#66BB62] w-fit rounded-sm'
          break
        case 'referred':
        default:
          className =
            'font-bold text-[white] px-2.5 text-sm py-1 bg-[#25262B] w-fit rounded-sm'
          break
      }

      return <p className={className}>{name}</p>
    },
    [t],
  )

  const columns = useMemo(
    () => [
      <span key="dewicat" className="ml-3 w-auto">
        {t('holdings.details.table.dewicat')}
      </span>,
      <span key="date" className="w-full">
        {t('holdings.details.table.date')}
      </span>,
      <span key="status" className="w-full">
        {t('holdings.details.table.status')}
      </span>,
      <span key="tokenEarned" className="flex gap-2 items-center w-full">
        <p>{t('holdings.details.tokenEarned')}</p>
      </span>,
      <div
        className="flex gap-2 justify-end w-full"
        key="claim-all-staking-rewards"
      >
        <ClaimSolButton holding={holding} />
        <span key="status" className="flex gap-2 items-center">
          <p>{'Status'}</p>
        </span>
        <ClaimAllStakingRewardsButton
          listings={stakedListings}
          holding={holding}
          onClaimAll={onClaimAll}
        />
      </div>,
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, stakedListings, holding, onClaimAll],
  )

  const sortByDate = (a: StakedListing, b: StakedListing) => {
    const dateA = a.createdAt.toNumber() * 1000
    const dateB = b.createdAt.toNumber() * 1000
    return dateB - dateA
  }

  const rows = useMemo(() => {
    if (!stakedListings) return []

    return stakedListings
      .slice()
      .sort(sortByDate)
      .filter((listing) => listing.totalStakedCount.toNumber() > 0)
      .map((listing) => [
        <span
          key={`dewicat-${listing.nft.toBase58()}`}
          className="flex gap-3 items-center w-auto"
        >
          <RowCatName nft={listing.nft.toBase58()} />
        </span>,
        <span
          key={`dewicat-date-${new Date(
            listing.createdAt.toNumber() * 1000,
          ).toDateString()}`}
          className="text-nowrap w-full"
        >
          {new Date(listing.createdAt.toNumber() * 1000).toDateString()}
        </span>,
        <span
          key={`dewicat-status-${getStatus('staking')}`}
          className="text-nowrap w-full"
        >
          {getStatus('staking')}
        </span>,
        <span
          key={`reward-${listing.publicKey.toBase58()}`}
          className="flex flex-col text-right gap-1 w-full"
        >
          <StakedRewardAmountText
            listing={listing.publicKey}
            mint={listing.tokenMint}
            holding={holding}
          />
        </span>,
        <StakingBadge
          key={`staking-badge-${listing.publicKey.toBase58()}`}
          listing={listing}
          holding={holding}
        />,
        <span
          key={`claim-${listing.publicKey.toBase58()}`}
          className="flex justify-end w-full"
        ></span>,
      ])
  }, [stakedListings, holding, getStatus])

  return (
    <Table
      className={{
        table: 'mt-4 w-full',
        th: 'text-left',
        td: 'text-left',
      }}
      rows={rows}
      columns={columns}
      enablePagination
      rowsPerPage={5}
    />
  )
}

export default StakingTable
